import React from 'react';
import { useDroppable } from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import { cn, Text, Spinner } from '@chatsdet/ui';
import type { BoardColumnConfig, BoardColumnLayout } from './types';

interface BoardColumnProps<TColumnId extends string> {
  column: BoardColumnConfig<TColumnId>;
  itemIds: string[];
  /**
   * Whether the active drag is currently targeting this column. Derived globally
   * by the Board (not from this column's own useDroppable.isOver) so hovering
   * over a card inside the column still marks the column as the target —
   * closestCenter picks the card as `over`, which would otherwise leave the
   * column's own isOver false and the highlight off.
   */
  isOver: boolean;
  /** null = neutral, true = valid drop target, false = invalid */
  isValidTarget: boolean | null;
  /** 'columns' = vertical card list in a fixed-width column (default, tasks board).
   *  'rows' = full-width band with a horizontal card scroller (suite builder).
   *  'rail' = borderless vertical card list for the staging rail (suite builder). */
  orientation?: BoardColumnLayout;
  children: React.ReactNode;
}

export function BoardColumn<TColumnId extends string>({
  column,
  itemIds,
  isOver,
  isValidTarget,
  orientation = 'columns',
  children,
}: BoardColumnProps<TColumnId>) {
  const { setNodeRef } = useDroppable({
    id: `column-${column.id}`,
    data: { columnId: column.id },
  });

  const hasItems = itemIds.length > 0;
  const total = column.totalItems ?? itemIds.length;

  if (orientation === 'rail') {
    // Staging rail: a flush, full-height column with a contrasting surface and
    // a right border, so it reads as a distinct panel (vs. the bands, which
    // bleed onto the canvas). Cards stack vertically; the list scrolls if tall.
    return (
      <div
        className={cn(
          // Matches the flow planner's conversations sidebar surface.
          'flex h-full w-[20rem] shrink-0 flex-col border-r border-border bg-secondary transition-colors duration-200',
          isOver && isValidTarget && 'bg-primary/10',
        )}
      >
        {/* Label + controls */}
        <div className="flex items-center justify-between gap-2 border-b border-border/60 px-4 pb-3 pt-4">
          <div className="flex min-w-0 items-center gap-2">{column.label}</div>
          <div className="flex shrink-0 items-center gap-1">{column.headerExtra}</div>
        </div>

        <div
          ref={setNodeRef}
          className="flex min-h-0 flex-1 flex-col items-stretch gap-3 overflow-y-auto px-3 pb-4 pt-3"
        >
          {column.isLoading ? (
            <div className="flex flex-1 items-center justify-center py-10">
              <Spinner />
            </div>
          ) : !hasItems ? (
            <div className="flex flex-1 items-start py-8">
              <Text size="sm" className="text-muted-foreground/50">
                {column.emptyLabel ?? 'Drag tests here to stage them'}
              </Text>
            </div>
          ) : (
            <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
              {children}
            </SortableContext>
          )}
        </div>
      </div>
    );
  }

  if (orientation === 'rows') {
    // Borderless: the layer "bleeds" onto the canvas. A dotted divider with the
    // label sitting above it is the only separator between layers.
    return (
      <div
        className={cn(
          'flex w-full flex-col transition-opacity duration-200',
          isOver && isValidTarget === false && 'opacity-50',
          isValidTarget === false && !isOver && 'opacity-60',
        )}
      >
        {/* Label — sits above the dotted line */}
        <div className="flex items-center justify-between gap-2 px-1 pb-1.5">
          <div className="flex min-w-0 items-center gap-2">{column.label}</div>
          <div className="flex shrink-0 items-center gap-1">{column.headerExtra}</div>
        </div>

        {/* Dotted divider — highlights on a valid drag-over */}
        <div
          className={cn(
            'border-t border-dashed transition-colors duration-200',
            isOver && isValidTarget ? 'border-primary' : 'border-border',
          )}
        />

        {/* Cards bleed onto the canvas — no box, just a faint tint on drag-over */}
        <div
          ref={setNodeRef}
          className={cn(
            'flex min-h-[140px] items-stretch gap-3 overflow-x-auto rounded-lg px-1 pb-1 pt-3 transition-colors duration-200',
            isOver && isValidTarget && 'bg-primary/5',
          )}
        >
          {column.isLoading ? (
            <div className="flex flex-1 items-center justify-center py-10">
              <Spinner />
            </div>
          ) : !hasItems ? (
            <div className="flex flex-1 items-center py-8">
              <Text size="sm" className="text-muted-foreground/40">
                {column.emptyLabel ?? 'Drag tests here or use Add'}
              </Text>
            </div>
          ) : (
            <SortableContext items={itemIds} strategy={horizontalListSortingStrategy}>
              {children}
            </SortableContext>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        // No explicit height — stretch to the board container's full height via
        // the flex row's default `align-items: stretch`, so the column dividers
        // reach the bottom of the container even when sparsely filled.
        'flex flex-col min-w-[280px] w-[280px] shrink-0',
        'border-r border-border',
        'border-t-2 transition-colors duration-200',
        column.accentClass ?? 'border-t-zinc-400',
        isOver && isValidTarget && (column.dropClass ?? 'bg-foreground/5'),
        isOver && isValidTarget === false && 'opacity-50',
        isValidTarget === false && !isOver && 'opacity-60',
      )}
    >
      <div className="flex items-center justify-between px-3 py-2 border-b border-border/50">
        <div className="flex items-center gap-2">
          <Text size="xs" weight="medium" className="text-muted-foreground uppercase tracking-wider">
            {column.label}
          </Text>
          {column.headerExtra}
        </div>
        <Text size="xs" className="text-muted-foreground/60">
          {total}
        </Text>
      </div>

      <div ref={setNodeRef} className="flex-1 px-2 py-2 space-y-1.5 overflow-y-auto">
        {column.isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Spinner />
          </div>
        ) : !hasItems ? (
          <div className="flex items-center justify-center py-8">
            <Text size="sm" className="text-muted-foreground/40">
              {column.emptyLabel ?? 'No items'}
            </Text>
          </div>
        ) : (
          <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
            {children}
          </SortableContext>
        )}
      </div>
    </div>
  );
}
